import { citeste, scrie, sterge, toate } from "./db.js";
import { api } from "../api.js";

/**
 * MT19 — coada de fișe lucrate fără semnal.
 *
 * Regula întregului modul: telefonul NU inventează nimic ce ține de decizia
 * serverului — nici numărul de document, nici prețul, nici scăderea din stoc.
 * Ține doar ce a scris omul în hală și îl urcă la revenirea semnalului.
 */

/** Identificatorul dat de telefon. Face ca o retrimitere să nu nască un al doilea document. */
export function identificatorLocal() {
  return (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

export const STARE = {
  inLucru: "in-lucru",
  deUrcat: "de-urcat",
  urcata: "urcata",
  conflict: "conflict",
};

/** O fișă nouă, ținută în telefon. `vizitaId` lipsește când s-a deschis chiar în hală. */
export function fisaLocala({ vizitaId = null, vizitaNoua = null, fisaId = null }) {
  return {
    localId: identificatorLocal(),
    fisaId, vizitaId, vizitaNoua,
    oraSosire: new Date().toISOString(),
    oraStart: new Date().toISOString(),
    operatii: [],
    articole: [],
    stare: STARE.inLucru,
    ultimaEroare: null,
    creataLa: new Date().toISOString(),
  };
}

/**
 * Liniile fișei venite de la server, în vocabularul cozii.
 *
 * Scrise O SINGURĂ dată și exportate fiindcă le cere și ecranul de teren: două
 * copii ale aceleiași potriviri de câmpuri ar diverge la prima corectură.
 * Coada înlocuiește lista ÎNTREAGĂ, nu rândul — serverul nu cunoaște
 * identificatorii de linie ai telefonului —, deci ecranul trimite lista de
 * DUPĂ apăsare, nu diferența.
 */
export function operatiiCoada(fisa) {
  return (fisa?.operatii ?? []).map((o) => ({
    operatieId: o.operatieId,
    cantitate: Number(o.cantitate),
    timpRealMinute: o.timpRealMinute,
    observatie: o.observatie ?? undefined,
  }));
}

export function articoleCoada(fisa) {
  return (fisa?.articole ?? []).map((a) => ({
    articolId: a.articolId,
    cantitate: Number(a.cantitate),
    serie: a.serie ?? undefined,
    gestiuneId: a.gestiuneId ?? undefined,
  }));
}

export async function salveaza(fisa) {
  await scrie(fisa);
  return fisa;
}

export async function marcheazaDeUrcat(localId) {
  const f = await citeste(localId);
  if (!f) return null;
  const gata = { ...f, stare: STARE.deUrcat };
  await scrie(gata);
  return gata;
}

/** Ce mai are telefonul de urcat. Ce e deja urcat nu se numără. */
export async function deUrcat() {
  return (await toate()).filter((f) => f.stare === STARE.deUrcat);
}

export async function inLucru() {
  return (await toate()).filter((f) => f.stare === STARE.inLucru);
}

/** Ce s-a lovit de o fișă semnată între timp — omul trebuie să afle, nu să ghicească. */
export async function cuConflict() {
  return (await toate()).filter((f) => f.stare === STARE.conflict);
}

/**
 * Ce se trimite serverului. Se compune AICI, nu la salvare: câmpurile interne
 * ale cozii (`stare`, `ultimaEroare`, momentele proprii) n-au ce căuta în
 * cerere, iar DTO-ul le-ar respinge oricum.
 */
function pentruServer(f) {
  const corp = { localId: f.localId };
  const treci = (nume) => { if (f[nume] != null && f[nume] !== "") corp[nume] = f[nume]; };
  ["fisaId", "vizitaId", "vizitaNoua", "oraSosire", "oraStart", "oraFinish",
    "durataDeplasareMinute", "kmParcursi", "descriereConstatari", "remedieriEfectuate",
    "cauzaRadacina", "stareLaPlecare", "necesitaRevenire", "motivRevenire",
    "numeSemnatarClient", "semnaturaClient", "semnaturaTehnician",
  ].forEach(treci);

  // Lista pleacă și când e GOALĂ: „am scos ultima operație" e o schimbare la
  // fel de reală ca „am adăugat una". Trimisă doar dacă nu e goală, o ștergere
  // făcută fără semnal ar dispărea în tăcere, iar fișa urcată ar purta o linie
  // pe care omul tocmai o scosese.
  if (Array.isArray(f.operatii)) {
    corp.operatii = f.operatii.map((o) => ({
      operatieId: o.operatieId, cantitate: o.cantitate,
      timpRealMinute: o.timpRealMinute, observatie: o.observatie || undefined,
    }));
  }
  if (Array.isArray(f.articole)) {
    corp.articole = f.articole.map((a) => ({
      articolId: a.articolId, cantitate: a.cantitate, serie: a.serie || undefined,
      gestiuneId: a.gestiuneId || undefined,
    }));
  }
  // Momentul semnăturii e cel de pe TEREN. Serverul îl compară cu ceasul lui și
  // consemnează devierea — nu-l crede pe cuvânt, dar nici nu-l aruncă.
  if (f.numeSemnatarClient && f.momentSemnaturii) corp.momentSemnaturii = f.momentSemnaturii;
  return corp;
}

/**
 * Urcă tot ce a rămas, una câte una.
 *
 * În ORDINEA în care s-au lucrat, nu în paralel: două fișe ale aceluiași
 * tehnician pe aceeași vizită s-ar putea încurca, iar o urcare în paralel n-ar
 * mai spune care a fost ultima — chiar întrebarea de care atârnă regula
 * „ultimul câștigă".
 */
export async function sincronizeazaTot() {
  const raport = { urcate: 0, conflicte: 0, esuate: 0, detalii: [] };
  const deTrimis = (await deUrcat()).sort((a, b) => a.creataLa.localeCompare(b.creataLa));

  for (const f of deTrimis) {
    try {
      const r = await api.urcaFisaDeTeren(pentruServer(f));

      if (r.rezultat === "revenire" || r.rezultat === "respinsa") {
        /*
         * Fișa a fost semnată sau stornată între timp. NU se șterge din
         * telefon: omul trebuie să vadă ce s-a întâmplat cu munca lui, cu
         * explicația serverului — „a dispărut din listă" ar fi cel mai rău
         * răspuns posibil.
         */
        await scrie({
          ...f, stare: STARE.conflict,
          explicatie: r.explicatie ?? null,
          fisaRezultata: r.fisa?.id ?? null,
          numarRezultat: r.fisa?.numar ?? null,
        });
        raport.conflicte++;
        raport.detalii.push({ localId: f.localId, rezultat: r.rezultat, explicatie: r.explicatie });
        continue;
      }

      // Urcată: se păstrează scurt, cu numărul primit de la server, ca omul să
      // vadă confirmarea. Ștergerea o face ecranul, nu sincronizarea.
      await scrie({
        ...f, stare: STARE.urcata,
        fisaId: r.fisa?.id ?? f.fisaId,
        numarRezultat: r.fisa?.numar ?? null,
        ultimaEroare: null,
      });
      raport.urcate++;
    } catch (e) {
      /*
       * Rămâne în coadă. O fișă pierdută fiindcă n-a mers rețeaua e chiar
       * defectul pe care modulul îl previne — deci eșecul se consemnează și se
       * reîncearcă, niciodată nu se aruncă.
       */
      /*
       * `cerere` din `api.js` aruncă un OBIECT simplu (`{status, mesaj}`), nu
       * un `Error`. Citit ca `e.message`, mesajul ieșea „[object Object]" — un
       * eșec fără motiv, adică exact ce nu-i folosește omului din hală.
       */
      const motiv = e?.mesaj || e?.message || String(e);
      await scrie({ ...f, ultimaEroare: motiv });
      raport.esuate++;
      raport.detalii.push({ localId: f.localId, eroare: motiv, status: e?.status });
    }
  }
  return raport;
}

/**
 * Păstrează în telefon ce n-a putut pleca.
 *
 * Se cheamă din ecranul de teren, când o salvare pică din lipsă de semnal. Ține
 * fișa aşa cum o știe serverul (pașii deja salvați) PLUS schimbarea care n-a
 * apucat să plece — la urcare, serverul primește fișa întreagă și scrie peste,
 * fiindcă „ultimul câștigă" cât timp nu e semnată.
 *
 * O singură intrare per fișă: a doua salvare picată o îmbogățește pe prima, nu
 * face a doua fișă. Altfel un tehnician cu semnal prost ar aduna zece intrări
 * pentru aceeași lucrare, iar la urcare ar conta doar ultima.
 */
export async function pastreazaLocal(fisaDeServer, schimbare = {}) {
  const toateFisele = await toate();
  const existenta = toateFisele.find(
    (f) => f.fisaId === fisaDeServer.id && f.stare !== STARE.urcata);

  const dinServer = {
    fisaId: fisaDeServer.id,
    vizitaId: fisaDeServer.vizitaId ?? fisaDeServer.vizita?.id ?? null,
    oraSosire: fisaDeServer.oraSosire ?? undefined,
    oraStart: fisaDeServer.oraStart ?? undefined,
    oraFinish: fisaDeServer.oraFinish ?? undefined,
    durataDeplasareMinute: fisaDeServer.durataDeplasareMinute ?? undefined,
    kmParcursi: fisaDeServer.kmParcursi ?? undefined,
    descriereConstatari: fisaDeServer.descriereConstatari ?? undefined,
    remedieriEfectuate: fisaDeServer.remedieriEfectuate ?? undefined,
    cauzaRadacina: fisaDeServer.cauzaRadacina ?? undefined,
    stareLaPlecare: fisaDeServer.stareLaPlecare ?? undefined,
    necesitaRevenire: fisaDeServer.necesitaRevenire ?? undefined,
    motivRevenire: fisaDeServer.motivRevenire ?? undefined,
    operatii: operatiiCoada(fisaDeServer),
    articole: articoleCoada(fisaDeServer),
  };

  const baza = existenta ?? fisaLocala({ fisaId: fisaDeServer.id });

  /*
   * ORDINEA celor trei straturi e chiar miezul plasei, iar prima variantă o
   * avea greșită: `...dinServer` venea peste coadă.
   *
   * Ecranul lucrează cu fișa așa cum a încărcat-o ULTIMA oară de la server —
   * adică fără nimic din ce s-a scris de atunci fără semnal. Deci la a doua
   * salvare picată, instantaneul vechi al serverului ștergea prima modificare
   * din coadă: omul scria constatările, apoi semna, iar constatările se
   * întorceau la textul de dimineață. Fără nicio eroare nicăieri.
   *
   * Acum: serverul e cel mai vechi strat, coada e mai nouă decât el (ține
   * lucruri pe care serverul nu le-a văzut încă), iar schimbarea care tocmai a
   * picat e cea mai nouă dintre toate.
   */
  const scrise = (o) => Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined));

  const gata = {
    ...baza,
    ...scrise(dinServer),
    ...scrise(existenta ? {
      oraSosire: existenta.oraSosire, oraStart: existenta.oraStart,
      oraFinish: existenta.oraFinish,
      durataDeplasareMinute: existenta.durataDeplasareMinute,
      kmParcursi: existenta.kmParcursi,
      descriereConstatari: existenta.descriereConstatari,
      remedieriEfectuate: existenta.remedieriEfectuate,
      cauzaRadacina: existenta.cauzaRadacina,
      stareLaPlecare: existenta.stareLaPlecare,
      necesitaRevenire: existenta.necesitaRevenire,
      motivRevenire: existenta.motivRevenire,
      numeSemnatarClient: existenta.numeSemnatarClient,
      momentSemnaturii: existenta.momentSemnaturii,
    } : {}),
    ...scrise(schimbare),
    /*
     * Listele se înlocuiesc ÎNTREGI, în aceeași ordine: schimbarea, altfel ce
     * ține coada, altfel ce știe serverul. O îmbinare pe rânduri ar putea învia
     * o linie tocmai scoasă, iar `dinServer` ar șterge o operație adăugată în
     * hală acum zece minute.
     */
    operatii: schimbare.operatii ?? existenta?.operatii ?? dinServer.operatii,
    articole: schimbare.articole ?? existenta?.articole ?? dinServer.articole,
    stare: STARE.deUrcat,
  };
  await scrie(gata);
  return gata;
}

export async function uitaFisa(localId) {
  await sterge(localId);
}
